import React, { useState, useEffect } from 'react';
import { Box, TextField, Select, MenuItem, FormHelperText } from '@mui/material';

const NumericRangeFilter = ({ value, onChange, error, columnName }) => {
  // Parse the existing SQL filter if it exists
  const [operator, setOperator] = useState('between');
  const [minValue, setMinValue] = useState('');
  const [maxValue, setMaxValue] = useState('');
  const [singleValue, setSingleValue] = useState('');

  useEffect(() => {
    // Parse incoming SQL filter value
    if (value) {
      if (value.toLowerCase().includes('between')) {
        const matches = value.match(/BETWEEN\s+(-?\d+\.?\d*)\s+AND\s+(-?\d+\.?\d*)/i);
        if (matches) {
          setOperator('between');
          setMinValue(matches[1]);
          setMaxValue(matches[2]);
        }
      } else if (value.match(/[<>]=?\s*-?\d+\.?\d*/)) {
        const matches = value.match(/([<>]=?)\s*(-?\d+\.?\d*)/);
        if (matches) {
          setOperator(matches[1]);
          setSingleValue(matches[2]);
        }
      } else if (value.match(/=\s*-?\d+\.?\d*/)) {
        const matches = value.match(/=\s*(-?\d+\.?\d*)/);
        if (matches) {
          setOperator('=');
          setSingleValue(matches[1]);
        }
      }
    }
  }, [value]);

  const generateSQLFilter = () => {
    if (operator === 'between') {
      if (minValue && maxValue) {
        return `BETWEEN ${minValue} AND ${maxValue}`;
      }
    } else if (operator === '=' || operator === '>' || operator === '<' || operator === '>=' || operator === '<=') {
      if (singleValue) {
        return `${operator} ${singleValue}`;
      }
    }
    return '';
  };

  const handleChange = (field, newValue) => {
    // Keep track of current values
    let currentOperator = operator;
    let currentMinValue = minValue;
    let currentMaxValue = maxValue;
    let currentSingleValue = singleValue;

    if (field === 'operator') {
      currentOperator = newValue;
      // Reset values when switching operators
      if (newValue === 'between') {
        currentSingleValue = '';
      } else {
        currentMinValue = '';
        currentMaxValue = '';
      }
      setOperator(newValue);
    } else if (field === 'min') {
      currentMinValue = newValue;
      setMinValue(newValue);
    } else if (field === 'max') {
      currentMaxValue = newValue;
      setMaxValue(newValue);
    } else if (field === 'single') {
      currentSingleValue = newValue;
      setSingleValue(newValue);
    }

    // Generate SQL using current values instead of state
    let sqlFilter = '';
    if (currentOperator === 'between') {
      if (currentMinValue && currentMaxValue) {
        sqlFilter = `${columnName} BETWEEN ${currentMinValue} AND ${currentMaxValue}`;
      }
    } else if (currentSingleValue) {
      sqlFilter = `${columnName} ${currentOperator} ${currentSingleValue}`;
    }

    onChange(sqlFilter);
  };

  return (
    <Box sx={{ mt: 1 }}>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
        <Select
          value={operator}
          onChange={(e) => handleChange('operator', e.target.value)}
          size="small"
          sx={{ minWidth: 120 }}
        >
          <MenuItem value="between">Between</MenuItem>
          <MenuItem value="=">=</MenuItem>
          <MenuItem value=">">{'>'}</MenuItem>
          <MenuItem value="<">{'<'}</MenuItem>
          <MenuItem value=">=">{'>='}</MenuItem>
          <MenuItem value="<=">{'<='}</MenuItem>
        </Select>
        
        {operator === 'between' ? (
          <>
            <TextField
              value={minValue}
              onChange={(e) => handleChange('min', e.target.value)}
              placeholder="Min"
              size="small"
              type="number"
              error={error && !minValue}
            />
            <TextField
              value={maxValue}
              onChange={(e) => handleChange('max', e.target.value)}
              placeholder="Max"
              size="small"
              type="number"
              error={error && !maxValue}
            />
          </>
        ) : (
          <TextField
            value={singleValue}
            onChange={(e) => handleChange('single', e.target.value)}
            placeholder="Value"
            size="small"
            type="number"
            error={error && !singleValue}
          />
        )}
      </Box>
      {error && <FormHelperText error>This field is required</FormHelperText>}
    </Box>
  );
};

export default NumericRangeFilter; 